'use client'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { logout } from '@/app/actions/logout'
import { fmt } from '@/lib/currency'
import Avatar from '@/components/Avatar'
import { EXTRAS_NAV_ITEMS } from '@/lib/navItems'

interface Props {
  username: string
  displayName: string | null
  avatarUrl: string
  balance: string
  isAdmin: boolean
}

export default function NavDropdown({ username, displayName, avatarUrl, balance, isAdmin }: Props) {
  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const handleToggle = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setDropdownStyle({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  const balanceClass = parseFloat(balance) < 0 ? 'text-red-500' : 'text-green-500'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 sm:px-3 sm:py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <Avatar url={avatarUrl} username={username} size={26} />
        <span className={`hidden sm:inline max-w-[120px] truncate font-semibold ${balanceClass}`}>{fmt(parseFloat(balance))}</span>
        <svg
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="fixed w-48 rounded-xl bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black/5 dark:ring-white/10 py-1 z-50" style={dropdownStyle}>
          <Link href={`/profile/${encodeURIComponent(username)}`} onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <Avatar url={avatarUrl} username={username} size={32} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{displayName ?? username}</p>
              {displayName && <span className="text-xs text-gray-400 truncate block">@{username}</span>}
              <span className={`text-sm font-semibold ${balanceClass}`}>{fmt(parseFloat(balance))}</span>
            </div>
          </Link>
          {isAdmin && (
            <Link href="/admin" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              🛠️ Admin Panel
            </Link>
          )}
          <Link href="/settings" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            ⚙️ Settings
          </Link>
          <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
          {EXTRAS_NAV_ITEMS.filter(item => item.key !== 'nav:fantasy-dungeon-league').map(item => (
            <Link key={item.key} href={item.route} onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              {item.emoji} {item.alt_name ?? item.name}
            </Link>
          ))}
          <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
          <Link href="/about" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            📐 About
          </Link>
          <Link href="/credits" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            🎬 Credits
          </Link>
          <form action={logout}>
            <button type="submit" className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              🚪 Logout
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
